import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  useProjectDetail,
  useProjects,
  useTaskStatuses,
  useUsers,
  useDocumentation,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useUpdateTaskStatus,
  useUpdateProject,
  useDeleteProject,
  useAddProjectMember,
  useRemoveProjectMember,
  useSprints,
  useCreateSprint,
  useUpdateSprint,
  useDeleteSprint
} from '../hooks/api';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Users, CheckSquare, Clock, DollarSign, FolderOpen, Plus, Calendar, ArrowRight, FileText, Search, ChevronLeft, ChevronRight, Edit, Trash2, Kanban, BarChart3, List, Grid, Layout, Building, CheckCircle, User, X, Share2, Sparkles, Flag } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { toUTCPlus5, formatDueDate } from '../utils/time';
import { useNavigate } from 'react-router-dom';
import Documentation from './Documentation';
import ShareSettingsModal from './ShareSettingsModal';
import ScopeToTasksWizard from './ScopeToTasksWizard';
import TaskFormDrawer from './TaskFormDrawer';
import FormDrawer from './FormDrawer';

const ProjectDetail = ({ projectId, onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
//
  const { 
    data: projectData, 
    isLoading: projectLoading,
    refetch: fetchProjectDetails
  } = useProjectDetail(projectId);
  
  const { 
    data: projectsData, 
    isLoading: projectsLoading 
  } = useProjects();
  
  const { 
    data: statusesData, 
    isLoading: statusesLoading 
  } = useTaskStatuses();
  
  const { 
    data: usersData, 
    isLoading: usersLoading 
  } = useUsers();
  
  // State declarations - must be before hook calls that use them
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  
  const { 
    data: documentationData, 
    isLoading: documentationLoading 
  } = useDocumentation(projectId, documentSearchTerm);

  const { data: sprintsData } = useSprints(projectId);
  const sprints = sprintsData?.results || [];
  const sprintIdToName = Object.fromEntries((sprints || []).map(s => [s.id, s.name]));
  
  // Mutations
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const updateTaskStatusMutation = useUpdateTaskStatus();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const addProjectMemberMutation = useAddProjectMember();
  const removeProjectMemberMutation = useRemoveProjectMember();
  const createSprintMutation = useCreateSprint();
  const updateSprintMutation = useUpdateSprint();
  const deleteSprintMutation = useDeleteSprint();
  
  // Extract data from React Query responses
  const projects = projectsData?.results || [];
  const taskStatuses = statusesData || [];
  const allTeamMembers = Array.isArray(usersData) ? usersData : (usersData?.results || []);
  const recentDocuments = documentationData?.results || [];
  const documentCount = documentationData?.total || 0;
  
  // UI State (not data state)
  const [loading, setLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberToAdd, setMemberToAdd] = useState(null);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isAssigningMember, setIsAssigningMember] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [currentDocumentPage, setCurrentDocumentPage] = useState(1);
  const [documentsPerPage, setDocumentsPerPage] = useState(5);
  const [documentViewMode, setDocumentViewMode] = useState('list'); // 'list', 'tiles'
  const [taskViewMode, setTaskViewMode] = useState('board'); // 'list', 'board', 'gantt'
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showScopeWizard, setShowScopeWizard] = useState(false);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [sprintToDelete, setSprintToDelete] = useState(null);
  const [sprintFormData, setSprintFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    goal: ''
  });

  // Task search and pagination state
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const tasksPerPage = 10;

  // Project edit/delete state
  const [showProjectEditModal, setShowProjectEditModal] = useState(false);
  const [showProjectDeleteModal, setShowProjectDeleteModal] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    description: '',
    client: '',
    is_billable: true,
    hourly_rate: ''
  });

  // 🎯 NO MORE FETCH FUNCTIONS! React Query handles everything automatically
  
  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentTaskPage(1);
  }, [taskSearchTerm]);

  // Persist a task create/update from the TaskFormDrawer.
  const handleProjectDetailTaskSubmit = async (taskData) => {
    const payload = {
      ...taskData,
      project_id: taskData.project_id || project.id,
    };
    if (editingTask) {
      await updateTaskMutation.mutateAsync({ id: editingTask.id, data: payload });
      toast.success('Task updated successfully');
    } else {
      await createTaskMutation.mutateAsync(payload);
      toast.success('Task created successfully');
    }
    queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] });
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
  };

  const handleRemoveMember = async (memberId) => {
    setIsRemovingMember(true);
    try {
      await removeProjectMemberMutation.mutateAsync({ projectId, memberId });
      toast.success('Member removed and unassigned from all tasks');
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
    } catch (err) {
      toast.error('Failed to remove member');
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleRemoveMemberClick = (member) => {
    setMemberToRemove(member);
    setShowRemoveMemberModal(true);
  };

  const handleMoveToAssigned = async (memberId) => {
    setIsAssigningMember(true);
    try {
      await addProjectMemberMutation.mutateAsync({ projectId, memberId });
      toast.success('Member moved to assigned members');
      setShowAddMemberModal(false);
      setMemberToAdd(null);
    } catch (err) {
      toast.error('Failed to move member');
    } finally {
      setIsAssigningMember(false);
    }
  };

  const handleAddMemberClick = (member) => {
    setMemberToAdd(member);
    setShowAddMemberModal(true);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const memberId = e.dataTransfer.getData('text/plain');
    if (memberId) {
      await handleMoveToAssigned(memberId);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  // Drag & Drop functionality for Board view
  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      setIsUpdatingStatus(true);
      await updateTaskStatusMutation.mutateAsync({ taskId, status: newStatus });
      
      // Refresh project data to get updated tasks
      await fetchProjectDetails();
      
      toast.success('Task status updated successfully');
    } catch (error) {
      toast.error('Failed to update task status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDragStart = (e, taskId) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', taskId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback to the dragged element
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'rotate(5deg)';
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    setDragOverColumn(null);
    
    // Reset visual feedback
    e.target.style.opacity = '1';
    e.target.style.transform = 'rotate(0deg)';
  };

  const handleTaskDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, statusName) => {
    e.preventDefault();
    setDragOverColumn(statusName);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the column (not just moving to a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleTaskDrop = (e, targetStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    const taskId = e.dataTransfer.getData('text/plain');
    updateTaskStatus(taskId, targetStatus);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    setIsDeletingTask(true);
    try {
      await deleteTaskMutation.mutateAsync(taskToDelete.id);
      toast.success('Task deleted successfully');
      setShowDeleteModal(false);
      setTaskToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] }); // Refresh the project details
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const cancelDeleteTask = () => {
    setShowDeleteModal(false);
    setTaskToDelete(null);
  };

  // Project edit/delete handlers
  const handleEditProject = (project) => {
    setProjectFormData({
      name: project.name,
      description: project.description || '',
      client: project.client || '',
      is_billable: project.is_billable,
      hourly_rate: project.hourly_rate || ''
    });
    setShowProjectEditModal(true);
  };

  const handleDeleteProject = (projectId) => {
    setShowProjectDeleteModal(true);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectFormData.name || projectFormData.name.trim() === '') {
      toast.error('Please enter a project name');
      return;
    }
    
    if (!projectFormData.description || projectFormData.description.trim() === '') {
      toast.error('Please enter a project description');
      return;
    }
    
    setIsEditingProject(true);
    try {
      const projectData = {
        ...projectFormData,
        hourly_rate: projectFormData.hourly_rate ? parseFloat(projectFormData.hourly_rate) : null
      };

      await updateProjectMutation.mutateAsync({ id: projectId, data: projectData });
      toast.success('Project updated successfully');
      setShowProjectEditModal(false);
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] }); // Refresh the project details
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setIsEditingProject(false);
    }
  };

  const handleProjectDelete = async () => {
    setIsDeletingProject(true);
    try {
      await deleteProjectMutation.mutateAsync(projectId);
      toast.success('Project deleted successfully');
      setShowProjectDeleteModal(false);
      onBack(); // Go back to projects list
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete project');
    } finally {
      setIsDeletingProject(false);
    }
  };

  const handleCloseProjectEditModal = () => {
    setShowProjectEditModal(false);
    setProjectFormData({
      name: '',
      description: '',
      client: '',
      is_billable: true,
      hourly_rate: ''
    });
  };

  // Helper functions for project health indicators
  const getTimelineStatus = () => {
    const completionPercentage = (tasks.filter(t => t.status === 'Done').length / Math.max(tasks.length, 1)) * 100;
    const inProgressTasks = tasks.filter(t => t.status === 'In Progress').length;
    const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date()).length;
    
    if (completionPercentage === 100) return 'Complete';
    if (overdueTasks > 0) return 'Overdue';
    if (inProgressTasks > 0) return 'Active';
    return 'Planning';
  };

  const getTimelineStatusColor = () => {
    const status = getTimelineStatus();
    switch (status) {
      case 'Complete': return 'bg-green-500';
      case 'Overdue': return 'bg-red-500';
      case 'Active': return 'bg-blue-500';
      default: return 'bg-yellow-500';
    }
  };

  // Project health and alerts functions
  const getProjectHealthScore = () => {
    if (tasks.length === 0) {
      return 75; // Good score for planning phase (no tasks yet) - match Projects component
    }

    const completionRate = (tasks.filter(t => t.status === 'Done').length / tasks.length) * 100;
    const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date()).length;
    const overdueRate = (overdueTasks / tasks.length) * 100;
    const teamUtilization = assigned_members.length > 0 ? 
      (tasks.filter(t => t.status === 'In Progress').length / assigned_members.length) * 100 : 0;
    
    // More balanced scoring system
    let score = 0;
    
    // Base score from completion rate (0-60 points)
    score += completionRate * 0.6;
    
    // Penalty for overdue tasks (0 to -30 points max)
    const overduePenalty = Math.min(overdueRate * 0.3, 30);
    score -= overduePenalty;
    
    // Bonus for good team utilization (0 to +10 points)
    if (teamUtilization > 0 && teamUtilization <= 100) {
      score += 10; // Good utilization bonus
    } else if (teamUtilization > 100) {
      score -= (teamUtilization - 100) * 0.1; // Small penalty for overutilization
    }
    
    // Ensure score is between 0 and 100
    const finalScore = Math.max(0, Math.min(100, Math.round(score)));
    
    return finalScore;
  };

  const getProjectAlerts = () => {
    const alerts = [];
    const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date());
    const inProgressTasks = tasks.filter(t => t.status === 'In Progress').length;
    const teamUtilization = (inProgressTasks / Math.max(assigned_members.length, 1)) * 100;
    
    if (overdueTasks.length > 0) {
      alerts.push({
        type: 'error',
        message: `${overdueTasks.length} task${overdueTasks.length > 1 ? 's' : ''} overdue`
      });
    }
    
    if (teamUtilization > 100) {
      alerts.push({
        type: 'warning',
        message: 'Team overutilized - consider redistributing tasks'
      });
    }
    
    if (tasks.length === 0) {
      alerts.push({
        type: 'info',
        message: 'No tasks created yet - add tasks to track progress'
      });
    }
    
    if (assigned_members.length === 0) {
      alerts.push({
        type: 'info',
        message: 'No team members assigned - add members to collaborate'
      });
    }
    
    return alerts;
  };



  // Show loader while project data is loading
  if (projectLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error message only after loading is complete and no data is found
  if (!projectData && !projectLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-600">Project not found</p>
      </div>
    );
  }

  const { project, tasks, team_members, assigned_members } = projectData;

  // Filter tasks based on search term
  const filteredTasks = tasks.filter(task => 
    task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
    (task.description && task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()))
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredTasks.length / tasksPerPage);
  const startIndex = (currentTaskPage - 1) * tasksPerPage;
  const endIndex = startIndex + tasksPerPage;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  // Compact pagination: show first, last, and pages around current
  const getVisiblePages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentTaskPage - delta && i <= currentTaskPage + delta)) {
        range.push(i);
      }
    }
    for (const i of range) {
      if (l !== undefined && i - l !== 1) rangeWithDots.push(null);
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  // Filter and paginate documents
  const filteredDocuments = recentDocuments.filter(doc => 
    doc.title.toLowerCase().includes(documentSearchTerm.toLowerCase()) ||
    (doc.content && doc.content.toLowerCase().includes(documentSearchTerm.toLowerCase()))
  );

  // Calculate document pagination
  const totalDocumentPages = Math.ceil(filteredDocuments.length / documentsPerPage);
  const documentStartIndex = (currentDocumentPage - 1) * documentsPerPage;
  const documentEndIndex = documentStartIndex + documentsPerPage;
  const paginatedDocuments = filteredDocuments.slice(documentStartIndex, documentEndIndex);

  // Show documentation component if selected
  if (showDocumentation) {
    return (
      <Documentation 
        projectId={projectId}
        onBack={() => setShowDocumentation(false)}
      />
    );
  }

  // Helper functions moved inside so they can access assigned_members
  function getMemberName(assignedTo) {
    if (!assignedTo) return 'Unassigned';
    
    // Handle both single user ID and array of user IDs
    const userIds = Array.isArray(assignedTo) ? assignedTo : [assignedTo];
    
    if (userIds.length === 0) return 'Unassigned';
    
    const memberNames = userIds.map(userId => {
    const member = assigned_members.find((m) => m.id === userId);
      return member ? (member.full_name || member.username) : 'Unknown';
    });
    
    return memberNames.join(', ');
  }
  
  function getPriorityBadgeColor(priority) {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'urgent': return 'bg-red-600 text-white';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  }
  
  function getStatusBadgeColor(status) {
    switch ((status || '').toLowerCase()) {
      case 'in progress': return 'bg-blue-100 text-blue-700';
      case 'done': return 'bg-green-100 text-green-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      case 'to do': return 'bg-secondary-100 text-secondary-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  }

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-secondary-50 rounded-lg transition-all duration-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FolderOpen className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">{project.name}</h1>
                <p className="text-secondary-600 dark:text-secondary-400 text-sm">Project Details</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              project.is_billable 
                ? 'bg-green-100 text-green-700' 
                : 'bg-secondary-100 text-secondary-600'
            }`}>
              {project.is_billable ? 'Billable' : 'Internal'}
            </span>
            <div className="px-3 py-1 bg-secondary-100 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-sm font-medium text-secondary-900">Active</span>
              </div>
            </div>
            {(user?.role === 'admin' || user?.role === 'project_lead') && (
              <button
                type="button"
                onClick={() => setShowShareModal(true)}
                className="px-3 py-1 bg-secondary-100 rounded-lg text-sm font-medium text-secondary-900 hover:bg-secondary-200 transition-colors flex items-center gap-2"
                title="Share with client"
              >
                <Share2 className="h-3.5 w-3.5" />
                Share
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Project Information Card */}
      <div className="card mb-6">
        <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <FolderOpen className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Project Information</h2>
            </div>
            <div className="flex items-center gap-2">
            {(user?.role === 'admin' || (user?.role === 'project_lead' && project.created_by === user?.id)) && (
              <>
                <button
                  onClick={() => handleEditProject(project)}
                  className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Edit Project"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteProject(project.id)}
                  className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Project"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
              </div>
            </div>
        
        {project.description && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-secondary-700 mb-2">Description</h3>
            <div className="p-3 rounded-lg bg-secondary-50 dark:bg-secondary-700">
              <p className="text-secondary-600 dark:text-secondary-300 leading-relaxed text-sm">{project.description}</p>
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-secondary-50 dark:bg-secondary-700">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span className="text-xs font-semibold text-secondary-700 dark:text-secondary-300">Created</span>
              </div>
            <p className="text-secondary-600 dark:text-secondary-400 text-xs">{toUTCPlus5(project.created_at)}</p>
          </div>

          {project.creator_name && (
            <div className="p-3 rounded-lg bg-secondary-50 dark:bg-secondary-700">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                <span className="text-xs font-semibold text-secondary-700 dark:text-secondary-300">Created by</span>
              </div>
              <p className="text-secondary-600 dark:text-secondary-400 text-xs">{project.creator_name}</p>
            </div>
          )}

          {project.client && (
            <div className="p-3 rounded-lg bg-secondary-50">
              <div className="flex items-center gap-2 mb-1">
                <Building className="h-4 w-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700">Client</span>
                </div>
              <p className="text-secondary-600 text-xs">{project.client}</p>
              </div>
          )}

          {project.hourly_rate && (
            <div className="p-3 rounded-lg bg-secondary-50">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700">Hourly Rate</span>
            </div>
              <p className="text-secondary-600 text-xs">${project.hourly_rate}/hr</p>
            </div>
          )}

          <div className="p-3 rounded-lg bg-secondary-50">
            <div className="flex items-center gap-2 mb-1">
              <CheckSquare className="h-4 w-4 text-primary-600" />
              <span className="text-xs font-semibold text-secondary-700">Total Tasks</span>
            </div>
            <p className="text-secondary-600 text-xs">{tasks.length}</p>
          </div>

          <div className="p-3 rounded-lg bg-secondary-50">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-primary-600" />
              <span className="text-xs font-semibold text-secondary-700">Team Members</span>
            </div>
            <p className="text-secondary-600 text-xs">{assigned_members.length}</p>
          </div>
        </div>

        {/* Project Completion Progress */}
        <div className="border-t border-secondary-200 pt-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-secondary-700">Project Progress</h3>
            <div className="text-sm text-secondary-600">
              {Math.round((tasks.filter(t => t.status === 'Done').length / Math.max(tasks.length, 1)) * 100)}% Complete
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="w-full bg-secondary-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ 
                  width: `${(tasks.filter(t => t.status === 'Done').length / Math.max(tasks.length, 1)) * 100}%` 
                }}
              ></div>
            </div>
          </div>

          {/* Project Health Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Completion Status */}
            <div className="p-4 rounded-lg border border-secondary-200">
                <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <h4 className="text-sm font-semibold text-secondary-700">Completion</h4>
                  </div>
              <div className="text-2xl font-bold text-secondary-900">
                {tasks.filter(t => t.status === 'Done').length}/{tasks.length}
                </div>
              <div className="text-xs text-secondary-600">tasks completed</div>
              </div>

            {/* Timeline Status */}
            <div className="p-4 rounded-lg border border-secondary-200">
                <div className="flex items-center gap-3 mb-2">
                <div className={`w-3 h-3 rounded-full ${getTimelineStatusColor()}`}></div>
                <h4 className="text-sm font-semibold text-secondary-700">Timeline</h4>
                  </div>
              <div className="text-2xl font-bold text-secondary-900">
                {getTimelineStatus()}
                </div>
              <div className="text-xs text-secondary-600">project status</div>
              </div>

            {/* Team Utilization */}
            <div className="p-4 rounded-lg border border-secondary-200">
                <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <h4 className="text-sm font-semibold text-secondary-700">Team Load</h4>
                  </div>
              <div className="text-2xl font-bold text-secondary-900">
                {Math.round((tasks.filter(t => t.status === 'In Progress').length / Math.max(assigned_members.length, 1)) * 100)}%
                </div>
              <div className="text-xs text-secondary-600">utilization</div>
            </div>
          </div>
        </div>

        {/* Project Statistics */}
        <div className="border-t border-secondary-200 pt-4 mb-6">
          <h3 className="text-sm font-semibold text-secondary-700 mb-4">Project Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{tasks.filter(t => t.status === 'Done').length}</div>
              <div className="text-xs text-green-700 dark:text-green-300 font-medium">Completed Tasks</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{tasks.filter(t => t.status === 'In Progress').length}</div>
              <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">In Progress</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{tasks.filter(t => t.status === 'To Do').length}</div>
              <div className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">To Do</div>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{documentCount}</div>
              <div className="text-xs text-purple-700 dark:text-purple-300 font-medium">Documents</div>
            </div>
          </div>
        </div>

        {/* Team Workload */}
        <div className="border-t border-secondary-200 pt-4 mb-6">
          <h3 className="text-sm font-semibold text-secondary-700 mb-4">Team Workload</h3>
          <div className="space-y-3">
            {assigned_members.map((member, index) => {
              const memberTasks = tasks.filter(t => 
                Array.isArray(t.assigned_to) ? t.assigned_to.includes(member.id) : t.assigned_to === member.id
              );
              const inProgressTasks = memberTasks.filter(t => t.status === 'In Progress').length;
              const completedTasks = memberTasks.filter(t => t.status === 'Done').length;
              const totalTasks = memberTasks.length;
              const completionPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
              
              return (
                <div key={member.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary-600">
                      {(member.full_name || member.username || member.name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-secondary-700">
                        {member.full_name || member.username || member.name || 'Unknown User'}
                      </span>
                      <span className="text-xs text-secondary-600">{totalTasks} tasks</span>
                    </div>
                    <div className="w-full bg-secondary-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-300 bg-green-500"
                        style={{ width: `${completionPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-secondary-600 mt-1">
                      <span>{completedTasks} done</span>
                      <span>{inProgressTasks} active</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Project Health & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Project Health Score */}
          <div className="p-4 rounded-lg border border-secondary-200">
            <h3 className="text-sm font-semibold text-secondary-700 mb-3">Project Health</h3>
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-3">
                <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    className="text-secondary-200"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    stroke="currentColor"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={`${getProjectHealthScore() * 2.2} 220`}
                    className={`${getProjectHealthScore() >= 70 ? 'text-green-500' : getProjectHealthScore() >= 40 ? 'text-yellow-500' : 'text-red-500'}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-secondary-900">{getProjectHealthScore()}</span>
                </div>
              </div>
              <p className="text-sm text-secondary-600 mb-2">Health Score</p>
              
              {/* Health Score Explanation */}
              <div className="text-xs text-secondary-500 bg-secondary-50 rounded-lg p-3 max-w-xs mx-auto">
                <div className="font-medium text-secondary-700 mb-1">Score Breakdown:</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Completion:</span>
                    <span className="font-medium">+{Math.round((tasks.filter(t => t.status === 'Done').length / Math.max(tasks.length, 1)) * 100 * 0.6)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overdue:</span>
                    <span className="font-medium text-red-600">-{Math.min(Math.round((tasks.filter(t => t.due_date && new Date(t.due_date) < new Date()).length / Math.max(tasks.length, 1)) * 100 * 0.3), 30)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team Load:</span>
                    <span className="font-medium text-green-600">+{assigned_members.length > 0 && (tasks.filter(t => t.status === 'In Progress').length / assigned_members.length) * 100 <= 100 ? '10' : '0'}</span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-secondary-200">
                  <div className={`text-xs font-medium ${
                    getProjectHealthScore() >= 70 ? 'text-green-600' : 
                    getProjectHealthScore() >= 40 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {getProjectHealthScore() >= 70 ? '✓ Project is healthy' : 
                     getProjectHealthScore() >= 40 ? '⚠ Needs attention' : '⚠ Critical issues'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Project Alerts */}
          <div className="p-4 rounded-lg border border-secondary-200">
            <h3 className="text-sm font-semibold text-secondary-700 mb-3">Alerts & Issues</h3>
            <div className="space-y-2">
              {getProjectAlerts().map((alert, index) => (
                <div key={index} className={`p-2 rounded-lg text-xs ${
                  alert.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                  alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                  'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      alert.type === 'error' ? 'bg-red-500' :
                      alert.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                    {alert.message}
                  </div>
                </div>
              ))}
              {getProjectAlerts().length === 0 && (
                <div className="text-center py-4 text-secondary-500 text-sm">
                  <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-500" />
                  No issues detected
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="border-t border-secondary-200 pt-4">
          <h3 className="text-sm font-semibold text-secondary-700 mb-3">Recent Activity</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-3 text-xs text-secondary-600">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>Project created by {project.creator_name}</span>
              <span className="text-secondary-400">{toUTCPlus5(project.created_at)}</span>
            </div>
            {tasks.slice(0, 3).map((task, index) => (
              <div key={task.id} className="flex items-center gap-3 text-xs text-secondary-600">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Task "{task.name}" {task.status.toLowerCase()}</span>
                <span className="text-secondary-400">{toUTCPlus5(task.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:text-primary-600 transition-colors"
            onClick={() => navigate('/tasks')}
            title="Click to view all tasks"
          >
          <CheckSquare className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-secondary-900">Tasks ({filteredTasks.length})</h2>
          <ArrowRight className="h-4 w-4 text-primary-600" />
          </div>
          <div className="flex items-center gap-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={taskSearchTerm}
                onChange={(e) => setTaskSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
              />
            </div>
            
            {/* View Mode Selector */}
            <div className="flex items-center gap-1 bg-secondary-100 rounded-lg p-1">
              <button
                onClick={() => setTaskViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  taskViewMode === 'list'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-800'
                }`}
                title="List View - Traditional task list with details"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setTaskViewMode('board')}
                className={`p-2 rounded-md transition-colors ${
                  taskViewMode === 'board'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-800'
                }`}
                title="Board View - Kanban-style columns with drag & drop"
              >
                <Kanban className="h-4 w-4" />
              </button>
              <button
                onClick={() => setTaskViewMode('gantt')}
                className={`p-2 rounded-md transition-colors ${
                  taskViewMode === 'gantt'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-800'
                }`}
                title="Gantt Chart - Timeline view with progress tracking"
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
            
            {(user?.role === 'admin' || user?.role === 'project_lead') && (
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && (
                  <button
                    onClick={() => setShowScopeWizard(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                    title="AI Task Creator - Generate tasks from your scope document (PDF or Word)"
                  >
                    <Sparkles className="h-4 w-4" />
                    <span className="text-sm font-medium">AI Task Creator</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingTask(null);
                    setShowTaskModal(true);
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Task
                </button>
              </div>
            )}
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="text-center py-8">
            <CheckSquare className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
            <p className="text-secondary-600">
              {taskSearchTerm ? 'No tasks found matching your search' : 'No tasks assigned to this project yet'}
            </p>
          </div>
        ) : (
          <>
            {taskViewMode === 'list' && (
              <div className="space-y-3">
                {paginatedTasks.map((task) => (
              <div
                key={task.id}
                className="border border-secondary-200 rounded-lg p-4 hover:bg-primary-50 transition-colors cursor-pointer shadow-sm flex flex-col gap-2"
                onClick={() => navigate(`/tasks/${task.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-900">{task.name}</h3>
                    {task.description && (
                      <p className="text-sm text-secondary-600 mt-1 truncate max-w-xs">{task.description}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                  {task.estimated_hours && (
                      <div className="flex items-center gap-1 text-xs text-secondary-500">
                      <Clock className="h-3 w-3" />
                      <span>{task.estimated_hours}h</span>
                    </div>
                    )}
                    {task.due_date && (
                      <div className="flex items-center gap-1 text-xs text-secondary-500">
                        <span className="font-medium">Due:</span>
                        <span>{formatDueDate(task.due_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {task.sprint_id && sprintIdToName[task.sprint_id] && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
                      <Flag className="h-3 w-3" />
                      {sprintIdToName[task.sprint_id]}
                    </span>
                  )}
                  {task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                      Assigned: {getMemberName(task.assigned_to)}
                    </span>
                  )}
                  {task.assigned_to && !Array.isArray(task.assigned_to) && task.assigned_to && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                      Assigned: {getMemberName(task.assigned_to)}
                    </span>
                  )}
                  {(!task.assigned_to || (Array.isArray(task.assigned_to) && task.assigned_to.length === 0)) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-300 text-xs font-medium">
                      Assigned: Unassigned
                    </span>
                  )}
                  {task.priority_level && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadgeColor(task.priority_level)}`}>
                      {task.priority_level.charAt(0).toUpperCase() + task.priority_level.slice(1)}
                    </span>
                  )}
                  {task.status && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(task.status)}`}>
                      {task.status}
                    </span>
                  )}
                  <div className="flex items-center gap-1 text-primary-600">
                    <ArrowRight className="h-3 w-3" />
                    <span className="text-xs">View Details</span>
                  </div>
                </div>
                
                {/* Action buttons - Edit and Delete buttons for project leads on tasks they created */}
                <div className="flex items-center justify-end gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                  {(user?.role === 'admin' || (user?.role === 'project_lead' && task.created_by === user?.id)) && (
                    <>
                      <button
                        onClick={() => handleEditTask(task)}
                        className="p-1.5 text-secondary-400 hover:text-primary-600 transition-colors"
                        title="Edit Task"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task)}
                        className="p-1.5 text-secondary-400 hover:text-red-600 transition-colors"
                        title="Delete Task"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
              </div>
            )}

            {taskViewMode === 'board' && (
              <>
                {/* Board View Info */}
                <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Kanban className="h-3 w-3 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-secondary-900 mb-1">Board View (Kanban)</h4>
                      <p className="text-xs text-secondary-700 mb-2">
                        Drag tasks between columns to update their status. Click on tasks to view details.
                      </p>
                      <div className="flex items-center gap-4 text-xs text-secondary-600">
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                          To Do
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          In Progress
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          In QA
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          Done
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                  {taskStatuses.map(status => {
                  const tasksInStatus = filteredTasks.filter(task => (task.status || 'To Do') === status.name);
                  return (
                    <div 
                      key={status.name} 
                      className={`bg-secondary-50 dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-3 sm:p-4 min-h-[220px] sm:min-h-[300px] md:min-h-[400px] transition-all duration-200 ${
                        dragOverColumn === status.name ? 'bg-blue-50 border-blue-300 shadow-lg scale-[1.02]' : ''
                      }`}
                      onDragOver={handleTaskDragOver}
                      onDragEnter={(e) => handleDragEnter(e, status.name)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleTaskDrop(e, status.name)}
                    >
                      {/* Column Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            status.name === 'To Do' ? 'bg-gray-400' :
                            status.name === 'In Progress' ? 'bg-blue-500' :
                            status.name === 'In QA' ? 'bg-yellow-500' :
                            status.name === 'Done' ? 'bg-green-500' :
                            'bg-red-500'
                          }`}></div>
                          <h4 className="font-semibold text-secondary-900 text-sm">{status.name}</h4>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {tasksInStatus.length}
                        </span>
                      </div>
                      
                      {/* Tasks Column */}
                      <div className="space-y-3 min-h-[300px]">
                        {tasksInStatus.map(task => (
                          <div
                            key={task.id}
                            className="card hover:shadow-md transition-all duration-200 cursor-pointer group border-l-4 border-l-primary-200 hover:border-l-primary-500"
                            draggable
                            onDragStart={(e) => handleDragStart(e, task.id)}
                            onDragEnd={handleDragEnd}
                            onClick={() => navigate(`/tasks/${task.id}`)}
                          >
                            <div className="space-y-2">
                              <h5 className="font-medium text-sm text-secondary-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                                {task.name}
                              </h5>
                              {task.description && (
                                <p className="text-xs text-secondary-600 line-clamp-2">{task.description}</p>
                              )}
                              
                              {/* Task Meta */}
                              <div className="flex items-center justify-between text-xs text-secondary-500 flex-wrap gap-1">
                                {task.sprint_id && sprintIdToName[task.sprint_id] && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
                                    <Flag className="h-3 w-3" />
                                    {sprintIdToName[task.sprint_id]}
                                  </span>
                                )}
                                {task.estimated_hours && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {task.estimated_hours}h
                                  </span>
                                )}
                                {task.due_date && (
                                  <span className="text-xs">
                                    Due {formatDueDate(task.due_date)}
                                  </span>
                                )}
                              </div>
                              
                              {/* Priority Indicator */}
                              {task.priority_level && (
                                <div className="flex items-center gap-1">
                                  <div className={`w-2 h-2 rounded-full ${
                                    task.priority_level === 'high' ? 'bg-red-500' :
                                    task.priority_level === 'medium' ? 'bg-yellow-500' :
                                    'bg-green-500'
                                  }`}></div>
                                  <span className="text-xs text-secondary-500 capitalize">
                                    {task.priority_level} priority
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Empty State */}
                        {tasksInStatus.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-8 text-center">
                            <div className="w-12 h-12 rounded-full bg-secondary-200 flex items-center justify-center mb-2">
                              <CheckSquare className="h-6 w-6 text-secondary-400" />
                            </div>
                            <p className="text-xs text-secondary-500">No tasks</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Loading Overlay for Board View */}
              {isUpdatingStatus && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 shadow-xl flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    <span className="text-secondary-700 font-medium">Updating task status...</span>
                  </div>
                </div>
              )}
              </>
            )}

            {taskViewMode === 'gantt' && (
              <>
                {/* Gantt Chart Info */}
                <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <BarChart3 className="h-3 w-3 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-secondary-900 mb-1">Gantt Chart View</h4>
                      <p className="text-xs text-secondary-700 mb-2">
                        Timeline view showing task progress, dates, and completion status. Perfect for project planning and tracking.
                      </p>
                      <div className="flex items-center gap-4 text-xs text-secondary-600">
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                          0% - Not Started
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          50% - In Progress
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          75% - In QA
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          100% - Done
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                {/* Header — desktop */}
                <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-secondary-50 border-b border-secondary-200">
                  <div className="col-span-4 font-semibold text-secondary-900 text-sm">Task Name</div>
                  <div className="col-span-2 font-semibold text-secondary-900 text-sm">Status</div>
                  <div className="col-span-2 font-semibold text-secondary-900 text-sm">Start Date</div>
                  <div className="col-span-2 font-semibold text-secondary-900 text-sm">Due Date</div>
                  <div className="col-span-2 font-semibold text-secondary-900 text-sm">Progress</div>
                </div>
                
                {/* Tasks */}
                <div className="divide-y divide-secondary-200">
                  {filteredTasks.map(task => (
                    <div key={task.id} className="flex flex-col gap-2 p-4 md:grid md:grid-cols-12 md:gap-4 hover:bg-secondary-50 transition-colors group">
                      <div className="md:col-span-4">
                        <h5 className="font-medium text-sm text-secondary-900 group-hover:text-primary-600 transition-colors">
                          {task.name}
                        </h5>
                        {task.description && (
                          <p className="text-xs text-secondary-600 mt-1 line-clamp-1">{task.description}</p>
                        )}
                      </div>
                      <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.status === 'Done' ? 'bg-green-100 text-green-800' :
                          task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          task.status === 'In QA' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {task.status || 'To Do'}
                        </span>
                      </div>
                      <div className="md:col-span-2 text-sm text-secondary-600">
                        <span className="md:hidden text-xs text-secondary-400 mr-1">Start:</span>
                        {task.created_at ? new Date(task.created_at).toLocaleDateString() : '-'}
                      </div>
                      <div className="md:col-span-2 text-sm text-secondary-600">
                        <span className="md:hidden text-xs text-secondary-400 mr-1">Due:</span>
                        {task.due_date ? formatDueDate(task.due_date) : '-'}
                      </div>
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2">
                          <div className="w-full bg-secondary-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                task.status === 'Done' ? 'bg-green-500' :
                                task.status === 'In QA' ? 'bg-yellow-500' :
                                task.status === 'In Progress' ? 'bg-blue-500' :
                                'bg-gray-400'
                              }`}
                              style={{ 
                                width: `${task.status === 'Done' ? 100 : 
                                       task.status === 'In QA' ? 75 : 
                                       task.status === 'In Progress' ? 50 : 0}%` 
                              }}
                            ></div>
                          </div>
                          <span className="text-xs text-secondary-500 min-w-[2rem]">
                            {task.status === 'Done' ? '100%' : 
                             task.status === 'In QA' ? '75%' : 
                             task.status === 'In Progress' ? '50%' : '0%'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </>
            )}
          </>
        )}

        {/* Pagination Controls */}
        {filteredTasks.length > tasksPerPage && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6 pt-4 border-t border-secondary-200">
            <div className="text-sm text-secondary-600 shrink-0">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredTasks.length)} of {filteredTasks.length} tasks
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <button
                onClick={() => setCurrentTaskPage(prev => Math.max(prev - 1, 1))}
                disabled={currentTaskPage === 1}
                className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              
              <div className="flex items-center gap-1 flex-wrap">
                {getVisiblePages().map((page, idx) =>
                  page === null ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-2 text-secondary-400">…</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentTaskPage(page)}
                      className={`px-3 py-2 text-sm border rounded-lg shrink-0 ${
                        currentTaskPage === page
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-secondary-200 hover:bg-secondary-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
              
              <button
                onClick={() => setCurrentTaskPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentTaskPage === totalPages}
                className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showScopeWizard && (
        <ScopeToTasksWizard
          projectId={projectId}
          projectName={project?.name}
          onClose={() => setShowScopeWizard(false)}
          onSuccess={() => fetchProjectDetails()}
        />
      )}

      {/* Sprints Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Sprints ({sprints.length})</h2>
          </div>
          {(user?.role === 'admin' || user?.role === 'project_lead') && (
            <button
              onClick={() => {
                setEditingSprint(null);
                setSprintFormData({ name: '', start_date: '', end_date: '', goal: '' });
                setShowSprintModal(true);
              }}
              className="btn-primary flex items-center gap-2 py-2 px-3 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Sprint
            </button>
          )}
        </div>
        {sprints.length === 0 ? (
          <div className="text-center py-6">
            <Calendar className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
            <p className="text-secondary-600 text-sm">No sprints yet</p>
            <p className="text-secondary-500 text-xs mt-1">
              {(user?.role === 'admin' || user?.role === 'project_lead')
                ? 'Click "Add Sprint" above or use the AI Task Creator'
                : 'Sprints are created when you use the AI Task Creator'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sprints.map((sprint) => (
              <div
                key={sprint.id}
                className="p-3 bg-secondary-50 dark:bg-secondary-900/30 rounded-lg border-l-4 border-l-primary-500 group flex items-start justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-secondary-900 dark:text-white text-sm">{sprint.name}</p>
                  <p className="text-xs text-secondary-500 mt-0.5">
                    {String(sprint.start_date).slice(0, 10)} – {String(sprint.end_date).slice(0, 10)}
                  </p>
                  {sprint.goal && (
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1 line-clamp-2">{sprint.goal}</p>
                  )}
                </div>
                {(user?.role === 'admin' || user?.role === 'project_lead') && (
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSprint(sprint);
                        setSprintFormData({
                          name: sprint.name,
                          start_date: String(sprint.start_date).slice(0, 10),
                          end_date: String(sprint.end_date).slice(0, 10),
                          goal: sprint.goal || ''
                        });
                        setShowSprintModal(true);
                      }}
                      className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors"
                      title="Edit sprint"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSprintToDelete(sprint);
                      }}
                      className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Delete sprint"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sprint Add/Edit Drawer */}
      <FormDrawer
        open={showSprintModal}
        onClose={() => setShowSprintModal(false)}
        title={editingSprint ? 'Edit Sprint' : 'Add Sprint'}
        subtitle={editingSprint ? 'Update the sprint details.' : 'Plan a time-boxed block of work for this project.'}
        icon={<Flag className="h-5 w-5" />}
        iconColor="primary"
        widthClass="sm:max-w-md lg:max-w-lg"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowSprintModal(false)}
              className="btn-secondary"
              disabled={createSprintMutation.isPending || updateSprintMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sprint-form"
              disabled={createSprintMutation.isPending || updateSprintMutation.isPending}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {(createSprintMutation.isPending || updateSprintMutation.isPending) ? 'Saving…' : (editingSprint ? 'Update Sprint' : 'Create Sprint')}
            </button>
          </>
        )}
      >
            <form
              id="sprint-form"
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!sprintFormData.name.trim()) {
                  toast.error('Sprint name is required');
                  return;
                }
                if (!sprintFormData.start_date || !sprintFormData.end_date) {
                  toast.error('Start and end dates are required');
                  return;
                }
                if (new Date(sprintFormData.start_date) >= new Date(sprintFormData.end_date)) {
                  toast.error('End date must be after start date');
                  return;
                }
                try {
                  if (editingSprint) {
                    await updateSprintMutation.mutateAsync({
                      sprintId: editingSprint.id,
                      projectId,
                      data: {
                        name: sprintFormData.name.trim(),
                        start_date: new Date(sprintFormData.start_date).toISOString(),
                        end_date: new Date(sprintFormData.end_date).toISOString(),
                        goal: sprintFormData.goal.trim() || null
                      }
                    });
                    toast.success('Sprint updated');
                  } else {
                    await createSprintMutation.mutateAsync({
                      project_id: projectId,
                      name: sprintFormData.name.trim(),
                      start_date: new Date(sprintFormData.start_date).toISOString(),
                      end_date: new Date(sprintFormData.end_date).toISOString(),
                      goal: sprintFormData.goal.trim() || null
                    });
                    toast.success('Sprint created');
                  }
                  setShowSprintModal(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to save sprint');
                }
              }}
            >
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={sprintFormData.name}
                  onChange={e => setSprintFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="e.g. Sprint 1: Foundation"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={sprintFormData.start_date}
                    onChange={e => setSprintFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="input-field w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={sprintFormData.end_date}
                    onChange={e => setSprintFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="input-field w-full"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Goal</label>
                <textarea
                  value={sprintFormData.goal}
                  onChange={e => setSprintFormData(prev => ({ ...prev, goal: e.target.value }))}
                  className="input-field w-full min-h-[80px]"
                  placeholder="Sprint goal or description"
                  rows={3}
                />
              </div>
            </form>
      </FormDrawer>

      {/* Delete Sprint Confirmation */}
      {sprintToDelete && (
        <div className="modal-backdrop-full flex items-center justify-center p-4 z-[9999]" onClick={() => setSprintToDelete(null)}>
          <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">Delete Sprint?</h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
              Are you sure you want to delete &quot;{sprintToDelete.name}&quot;? Tasks in this sprint will be unassigned.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSprintToDelete(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={async () => {
                  try {
                    await deleteSprintMutation.mutateAsync(sprintToDelete.id);
                    toast.success('Sprint deleted');
                    setSprintToDelete(null);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to delete sprint');
                  }
                }}
                disabled={deleteSprintMutation.isPending}
                className="btn-primary bg-red-600 hover:bg-red-700"
              >
                {deleteSprintMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documentation Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div 
            className="flex items-center gap-2 cursor-pointer hover:text-primary-600 transition-colors"
            onClick={() => setShowDocumentation(true)}
            title="Click to view all documentation"
          >
            <FileText className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-secondary-900">Documentation ({filteredDocuments.length})</h2>
            <ArrowRight className="h-4 w-4 text-primary-600" />
          </div>
          <div className="flex items-center gap-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={documentSearchTerm}
                onChange={(e) => setDocumentSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
              />
            </div>
            
            {/* View Mode Selector */}
            <div className="flex items-center gap-1 bg-secondary-100 rounded-lg p-1">
              <button
                onClick={() => setDocumentViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  documentViewMode === 'list'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-800'
                }`}
                title="List View - Traditional document list with details"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDocumentViewMode('tiles')}
                className={`p-2 rounded-md transition-colors ${
                  documentViewMode === 'tiles'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-800'
                }`}
                title="Tiles View - Card-based layout"
              >
                <BarChart3 className="h-4 w-4" />
              </button>
            </div>
            
            {(user?.role === 'admin' || user?.role === 'project_lead') && (
          <button
            onClick={() => setShowDocumentation(true)}
            className="btn-primary flex items-center gap-2"
          >
                <Plus className="h-4 w-4" />
                New Document
          </button>
            )}
          </div>
        </div>
        
        {/* My Documents Section */}
        {filteredDocuments.filter(doc => doc.created_by === user?.id).length > 0 && (
          <div className="mb-6">
      
            {documentViewMode === 'list' ? (
              <div className="space-y-2">
                {filteredDocuments.filter(doc => doc.created_by === user?.id).slice(0, 3).map((doc) => (
                  <div 
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer border border-blue-200"
                    onClick={() => setShowDocumentation(true)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                       <div>
                         <h4 className="font-medium text-secondary-900 text-sm">{doc.title}</h4>
                         <div className="flex items-center gap-2 text-xs text-secondary-500">
                           <span>{doc.created_by_name}</span>
                           <span>•</span>
                           <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                           {doc.is_public ? (
                             <span className="text-green-600">• Public</span>
                           ) : (
                             <span className="text-orange-600">• Private</span>
                           )}
                         </div>
                       </div>
                    </div>
                    <ArrowRight className="h-3 w-3 text-secondary-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDocuments.filter(doc => doc.created_by === user?.id).slice(0, 3).map((doc) => (
                  <div 
                    key={doc.id}
                    className="card hover:shadow-lg transition-all duration-200 cursor-pointer group h-full flex flex-col border border-blue-200 bg-blue-50"
                    onClick={() => setShowDocumentation(true)}
                  >
                    <div className="space-y-3 flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FileText className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-secondary-900 truncate group-hover:text-blue-600 transition-colors">
                                {doc.title}
                              </h3>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            {doc.is_public ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Public
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                Private
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-secondary-500 mt-auto">
                        <div className="flex items-center gap-1">
                          <span className="text-secondary-400">👤</span>
                          <span className="truncate">{doc.created_by_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-secondary-400">📅</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {filteredDocuments.length > 0 ? (
          <>
            {documentViewMode === 'list' && (
          <div className="space-y-3">
                {paginatedDocuments.filter(doc => doc.created_by !== user?.id).map((doc) => (
              <div 
                key={doc.id}
                className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors cursor-pointer"
                onClick={() => setShowDocumentation(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-4 w-4 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900">{doc.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-secondary-500">
                      <span>{doc.created_by_name}</span>
                      <span>•</span>
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      {doc.is_public ? (
                        <span className="text-green-600">• Public</span>
                      ) : (
                        <span className="text-orange-600">• Private</span>
                      )}
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-secondary-400" />
              </div>
            ))}
              </div>
            )}

            {documentViewMode === 'tiles' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedDocuments.filter(doc => doc.created_by !== user?.id).map((doc) => (
                  <div 
                    key={doc.id}
                    className="card hover:shadow-lg transition-all duration-200 cursor-pointer group h-full flex flex-col"
                    onClick={() => setShowDocumentation(true)}
                  >
                    <div className="space-y-3 flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                              <FileText className="h-4 w-4 text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-secondary-900 truncate group-hover:text-primary-600 transition-colors">
                                {doc.title}
                              </h3>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            {doc.is_public ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                Public
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                Private
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-secondary-500 mt-auto">
                        <div className="flex items-center gap-1">
                          <span className="text-secondary-400">👤</span>
                          <span className="truncate">{doc.created_by_name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-secondary-400">📅</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination Controls */}
            {totalDocumentPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                <div className="text-sm text-secondary-600">
                  Showing {documentStartIndex + 1} to {Math.min(documentEndIndex, filteredDocuments.length)} of {filteredDocuments.length} documents
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentDocumentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentDocumentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalDocumentPages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentDocumentPage(page)}
                          className={`px-3 py-2 text-sm border rounded-lg ${
                            currentDocumentPage === page
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'border-secondary-200 hover:bg-secondary-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentDocumentPage(prev => Math.min(prev + 1, totalDocumentPages))}
                    disabled={currentDocumentPage === totalDocumentPages}
                    className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            
            {filteredDocuments.length > documentsPerPage && (
              <div className="text-center pt-2">
                <button
                  onClick={() => setShowDocumentation(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View all {filteredDocuments.length} documents →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
            <p className="text-secondary-600 mb-4">
              {documentSearchTerm 
                ? `No documents match your search for "${documentSearchTerm}"`
                : 'Create and manage project documentation, guides, and knowledge base'
              }
            </p>
            {documentSearchTerm ? (
              <button
                onClick={() => setDocumentSearchTerm('')}
                className="btn-secondary"
              >
                Clear Search
              </button>
            ) : (
            <button
              onClick={() => setShowDocumentation(true)}
              className="btn-secondary"
            >
              Manage Documentation
            </button>
            )}
          </div>
        )}
      </div>

      {/* Assigned Members Section */}
      <div 
        className="card"
        onDrop={(user?.role === 'admin' || user?.role === 'project_lead') ? handleDrop : undefined}
        onDragOver={(user?.role === 'admin' || user?.role === 'project_lead') ? handleDragOver : undefined}
        onDragEnter={(user?.role === 'admin' || user?.role === 'project_lead') ? (e) => {
          e.preventDefault();
          e.currentTarget.classList.add('bg-blue-50', 'border-blue-300');
        } : undefined}
        onDragLeave={(user?.role === 'admin' || user?.role === 'project_lead') ? (e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300');
        } : undefined}
      >
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-secondary-900">Assigned Members ({assigned_members.length})</h2>
          {(user?.role === 'admin' || user?.role === 'project_lead') && (
            <span className="text-xs text-secondary-500 ml-2">Drop team members here to assign them</span>
          )}
        </div>

        {assigned_members.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
            <p className="text-secondary-600">No members have been assigned to this project yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {assigned_members.map((member) => (
              <div key={member.id} className="border border-secondary-200 rounded-lg p-3 hover:bg-secondary-50 transition-colors">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-medium text-xs">
                      {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-secondary-900 text-sm truncate">
                      {member.full_name || member.username}
                      {member.id === user?.id && ' (You)'}
                    </h3>
                    <p className="text-xs text-secondary-600 truncate">{member.email}</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${member.role === 'admin' ? 'bg-red-100 text-red-700' : member.role === 'project_lead' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{member.role === 'project_lead' ? 'Project Lead' : member.role}</span>
                  </div>
                  {(user?.role === 'admin' || user?.role === 'project_lead') && user.id !== member.id && (
                    <button
                       className="ml-auto bg-red-600 hover:bg-red-700 text-white px-2 py-1 text-xs rounded transition-colors flex-shrink-0"
                       onClick={() => handleRemoveMemberClick(member)}
                    >
                       Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Team Members Section - Only visible to Project Leads and Admins */}
      {(user?.role === 'admin' || user?.role === 'project_lead') && (
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-secondary-900">Available Team Members</h2>
            <span className="text-xs text-secondary-500 ml-2">Members not currently assigned to this project</span>
        </div>

                     {/* Filter out members who are already assigned to this project */}
           {(() => {
            
             
             const availableMembers = allTeamMembers.filter(member => 
               !assigned_members.find(assigned => assigned.id === member.id)
             );
             
             
             return availableMembers.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
                 <p className="text-secondary-600">All team members are already assigned to this project</p>
                 <p className="text-xs text-secondary-500 mt-2">
                   Total system members: {allTeamMembers.length} | Assigned: {assigned_members.length}
                 </p>
          </div>
        ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                 {availableMembers.map((member) => (
              <div 
                key={member.id} 
                     className="border border-secondary-200 rounded-lg p-3 hover:bg-secondary-50 transition-colors cursor-move"
                     draggable={true}
                     onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', member.id);
                  e.dataTransfer.effectAllowed = 'move';
                     }}
                     title="Drag to move to assigned members"
                   >
                     <div className="flex items-center gap-2">
                       <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                         <span className="text-primary-600 font-medium text-xs">
                      {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                    </span>
                  </div>
                       <div className="flex-1 min-w-0">
                         <h3 className="font-medium text-secondary-900 text-sm truncate">
                      {member.full_name || member.username}
                      {member.id === user?.id && ' (You)'}
                    </h3>
                         <p className="text-xs text-secondary-600 truncate">{member.email}</p>
                                                   <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${member.role === 'admin' ? 'bg-red-100 text-red-700' : member.role === 'project_lead' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{member.role === 'project_lead' ? 'Project Lead' : member.role}</span>
                       </div>
                                               <button
                          className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 text-xs rounded transition-colors flex-shrink-0"
                          onClick={() => handleAddMemberClick(member)}
                          title="Add to project"
                        >
                          Add
                        </button>
                     </div>
                   </div>
                 ))}
               </div>
             );
           })()}
        </div>
      )}



             {/* Remove Member Confirmation Modal */}
       {showRemoveMemberModal && memberToRemove && (
         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
           <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-md mx-4 p-6 shadow-lg relative">
             <div className="flex items-center gap-3 mb-4">
               <Users className="h-6 w-6 text-red-500" />
               <h2 className="text-xl font-semibold text-secondary-900">Confirm Member Removal</h2>
             </div>
             
             <div className="mb-6">
               <p className="text-secondary-600 mb-4">
                 Are you sure you want to remove <strong>{memberToRemove.full_name || memberToRemove.username}</strong> from this project?
               </p>
               
               <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                 <div className="flex items-center gap-2">
                   <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                     <span className="text-primary-600 font-medium text-xs">
                       {memberToRemove.full_name ? memberToRemove.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : memberToRemove.username[0].toUpperCase()}
                      </span>
                   </div>
                   <div>
                     <p className="font-medium text-secondary-900 text-sm">{memberToRemove.full_name || memberToRemove.username}</p>
                     <p className="text-xs text-secondary-600">{memberToRemove.email}</p>
                     <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${memberToRemove.role === 'admin' ? 'bg-red-100 text-red-700' : memberToRemove.role === 'project_lead' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                       {memberToRemove.role === 'project_lead' ? 'Project Lead' : memberToRemove.role}
                        </span>
                    </div>
                  </div>
               </div>
               
               <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                 <p className="text-sm text-yellow-800">
                   <strong>Warning:</strong> This will also unassign them from all tasks in this project.
                 </p>
               </div>
             </div>
             
             <div className="flex gap-3 justify-end">
                    <button
                 type="button"
                 onClick={() => {
                   setShowRemoveMemberModal(false);
                   setMemberToRemove(null);
                 }}
                 className="btn-secondary px-4 py-2"
               >
                 Cancel
                    </button>
               <button
                 type="button"
                 onClick={() => handleRemoveMember(memberToRemove.id)}
                 disabled={isRemovingMember}
                 className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isRemovingMember ? (
                   <div className="flex items-center gap-2">
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                     Removing...
                   </div>
                 ) : (
                   'Confirm Removal'
                 )}
               </button>
                </div>
              </div>
          </div>
        )}

       {/* Add Member Confirmation Modal */}
       {showAddMemberModal && memberToAdd && (
         <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
           <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-md mx-4 p-6 shadow-lg relative">
             <div className="flex items-center gap-3 mb-4">
               <Users className="h-6 w-6 text-primary-600" />
               <h2 className="text-xl font-semibold text-secondary-900">Confirm Member Assignment</h2>
      </div>
             
             <div className="mb-6">
               <p className="text-secondary-600 mb-4">
                 Are you sure you want to assign <strong>{memberToAdd.full_name || memberToAdd.username}</strong> to this project?
               </p>
               
               <div className="bg-secondary-50 rounded-lg p-3 border border-secondary-200">
                 <div className="flex items-center gap-2">
                   <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                     <span className="text-primary-600 font-medium text-xs">
                       {memberToAdd.full_name ? memberToAdd.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : memberToAdd.username[0].toUpperCase()}
                     </span>
                   </div>
                   <div>
                     <p className="font-medium text-secondary-900 text-sm">{memberToAdd.full_name || memberToAdd.username}</p>
                     <p className="text-xs text-secondary-600">{memberToAdd.email}</p>
                     <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${memberToAdd.role === 'admin' ? 'bg-red-100 text-red-700' : memberToAdd.role === 'project_lead' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                       {memberToAdd.role === 'project_lead' ? 'Project Lead' : memberToAdd.role}
                     </span>
                   </div>
                 </div>
               </div>
             </div>
             
             <div className="flex gap-3 justify-end">
               <button
                 type="button"
                 onClick={() => {
                   setShowAddMemberModal(false);
                   setMemberToAdd(null);
                 }}
                 className="btn-secondary px-4 py-2"
               >
                 Cancel
               </button>
               <button
                 type="button"
                 onClick={() => handleMoveToAssigned(memberToAdd.id)}
                 disabled={isAssigningMember}
                 className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {isAssigningMember ? (
                   <div className="flex items-center gap-2">
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                     Assigning...
                   </div>
                 ) : (
                   'Confirm Assignment'
                 )}
               </button>
             </div>
           </div>
         </div>
       )}

      {/* Task Creation/Edit Drawer */}
      <TaskFormDrawer
        open={showTaskModal}
        onClose={handleCloseTaskModal}
        editingTask={editingTask}
        currentUser={user}
        onSubmit={handleProjectDetailTaskSubmit}
        initialProjectId={project?.id || ''}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={cancelDeleteTask}>
          <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-md mx-4 flex flex-col shadow-lg relative" onClick={e => e.stopPropagation()}>
            {/* Close (X) icon at top right */}
            <button
              type="button"
              className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-700 text-2xl focus:outline-none"
              onClick={cancelDeleteTask}
              aria-label="Close"
            >
              &times;
            </button>
            
            <div className="flex items-center gap-3 px-6 py-4 border-b border-secondary-200">
              <Trash2 className="h-6 w-6 text-red-500" />
              <h2 className="text-xl font-semibold text-secondary-900">Delete Task</h2>
            </div>
            
            <div className="p-6">
              <p className="text-secondary-600 mb-4">
                Are you sure you want to delete the task <strong>"{taskToDelete?.name}"</strong>? 
                This action cannot be undone.
              </p>
              
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelDeleteTask}
                  className="btn-secondary"
                  disabled={isDeletingTask}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteTask}
                  disabled={isDeletingTask}
                  className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingTask ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </div>
                  ) : (
                    'Delete Task'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Edit Modal */}
      {showProjectEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleCloseProjectEditModal}>
          <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-xl mx-4 my-16 flex flex-col shadow-lg relative" onClick={e => e.stopPropagation()}>
            {/* Close (X) icon at top right */}
            <button
              type="button"
              className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-700 text-2xl focus:outline-none"
              onClick={handleCloseProjectEditModal}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-3 px-6 py-4 border-b border-secondary-200">
              <FolderOpen className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-secondary-900">Edit Project</h2>
            </div>
            <div className="flex-1 p-6 max-h-[50vh] overflow-y-auto">
              <form onSubmit={handleProjectSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Project Name *</label>
                    <input
                      type="text"
                      className="input-field"
                      value={projectFormData.name}
                      onChange={(e) => setProjectFormData({...projectFormData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Client</label>
                    <input
                      type="text"
                      className="input-field"
                      value={projectFormData.client}
                      onChange={(e) => setProjectFormData({...projectFormData, client: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Description *</label>
                  <textarea
                    className="input-field"
                    rows="3"
                    value={projectFormData.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 250) {
                        setProjectFormData({...projectFormData, description: value});
                      }
                    }}
                    maxLength={250}
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-secondary-500">
                      <span className="font-bold">
                        {projectFormData.description ? projectFormData.description.length : 0}
                      </span>
                      /250
                    </span>
                    <span className="text-xs text-secondary-500">
                      Max 250 characters
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-end">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={projectFormData.is_billable}
                        onChange={(e) => setProjectFormData({...projectFormData, is_billable: e.target.checked})}
                        className="rounded border-secondary-300"
                      />
                      <span className="text-sm font-medium text-secondary-700">Billable</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Hourly Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      value={projectFormData.hourly_rate}
                      onChange={(e) => setProjectFormData({...projectFormData, hourly_rate: e.target.value})}
                    />
                  </div>
                </div>
              </form>
            </div>
            
            {/* Fixed Footer with Buttons */}
            <div className="px-6 py-4 border-t border-secondary-200">
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={handleCloseProjectEditModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isEditingProject}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleProjectSubmit}
                >
                  {isEditingProject ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </div>
                  ) : (
                    'Update Project'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Delete Confirmation Modal */}
      {showProjectDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-secondary-800 rounded-xl w-full max-w-md mx-4 flex flex-col shadow-lg relative">
            {/* Close (X) icon at top right */}
            <button
              type="button"
              className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-700 text-2xl focus:outline-none"
              onClick={() => setShowProjectDeleteModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            
            <div className="flex items-center gap-3 px-6 py-4 border-b border-secondary-200">
              <Trash2 className="h-6 w-6 text-red-500" />
              <h2 className="text-xl font-semibold text-secondary-900">Delete Project</h2>
            </div>
            
            <div className="p-6">
              <p className="text-secondary-600 mb-4">
                Are you sure you want to delete the project <strong>"{project.name}"</strong>? 
                This action cannot be undone and will also delete all associated tasks and time entries.
              </p>
              
              <div className="bg-red-50 rounded-lg p-3 border border-red-200 mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FolderOpen className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900 text-sm">{project.name}</p>
                    <p className="text-xs text-secondary-600">{project.description}</p>
                    {project.client && (
                      <p className="text-xs text-secondary-600">Client: {project.client}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowProjectDeleteModal(false)}
                  className="btn-secondary"
                  disabled={isDeletingProject}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleProjectDelete}
                  disabled={isDeletingProject}
                  className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingProject ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </div>
                  ) : (
                    'Delete Project'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share with client modal */}
      <ShareSettingsModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        projectId={projectId}
        projectName={project?.name}
      />
    </div>
  );
};

export default ProjectDetail; 